<template>
  <button
    :aria-busy="loading"
    :aria-label="props.ariaLabel"
    :class="classes"
    :disabled="disabled || loading"
    :role="role"
    :data-testid="props.dataTestid"
    type="button"
    @click="handleOnClick"
    @mousedown="handlePressStart"
    @mouseleave="handlePressEnd"
    @mouseup="handlePressEnd"
    @touchend="handlePressEnd"
    @touchstart="handlePressStart"
  >
    <span
      v-if="props.icon?.src && props.icon?.position === 'left' && !loading"
      class="icon"
    >
      <component :is="{ ...props.icon.src }" />
    </span>
    <span class="content">
      <slot></slot>
    </span>
    <span
      v-if="props.icon?.src && props.icon?.position === 'right' && !loading"
      class="icon"
    >
      <component :is="{ ...props.icon.src }" />
    </span>
    <span v-if="loading" class="loading-container"
      ><span class="loader"></span
    ></span>
  </button>
</template>

<script setup>
import { computed, ref } from 'vue';
import '@/v2/design-system/styles/main.css';

const props = defineProps({
  role: { type: String, default: 'button' },
  ariaLabel: String,
  icon: {
    type: Object,
    default: null,
    validator: function (value) {
      if (!value) return true;

      if (
        !value.src ||
        (typeof value.src !== 'string' && typeof value.src !== 'object')
      )
        return false;

      return !(
        value.position && ['left', 'right'].indexOf(value.position) === -1
      );
    }
  },
  size: {
    type: String,
    validator: function (value) {
      return ['small', 'medium', 'large'].indexOf(value) !== -1;
    }
  },
  type: {
    type: String,
    validator: function (value) {
      return (
        ['primary', 'secondary', 'tertiary', 'danger'].indexOf(value) !== -1
      );
    }
  },
  disabled: {
    type: Boolean
  },
  onClick: {
    type: Function,
    required: true
  },
  loading: {
    type: Boolean
  },
  dataTestid: {
    type: String
  }
});

const clicked = ref(false);

const classes = computed(() => ({
  button: true,
  'button--primary': props.type === 'primary',
  'button--secondary': props.type === 'secondary',
  'button--tertiary': props.type === 'tertiary',
  'button--danger': props.type === 'danger',
  'button--clicked': clicked.value,
  'button--disabled': props.disabled,
  'button--loading': props.loading,
  [`button--${props.size || 'medium'}`]: true
}));

function handlePressStart() {
  if (props.disabled) return;
  clicked.value = true;
}

function handlePressEnd() {
  if (props.disabled) return;
  clicked.value = false;
}

function handleOnClick(e) {
  props.onClick && props.onClick(e);
}
</script>

<style scoped>
.icon {
  display: flex;

  & > svg {
    height: var(--icon-size-i-16);
    width: var(--icon-size-i-16);
  }
}

.button {
  font-family: var(--body-small-reg-family), sans-serif;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  cursor: pointer;
  border: 0;
  padding: var(--pad-space-sp-12) var(--pad-space-sp-24);
  line-height: 1;
  position: relative;
}

.button--primary {
  border-radius: var(--corner-radius-c-8);
  background: var(--primary-surface-default);
  color: var(--primary-text-icon-negative);
  font-size: var(--body-base-med-size);
  font-weight: var(--body-base-med-weight);
  line-height: var(--line-height-lead-md);
}

.button--primary:focus-visible {
  outline: 2px solid var(--primary-surface-default);
  outline-offset: 2px;
}

.button--primary.button--disabled {
  background-color: var(--neutral-surface-disabled);
  color: var(--neutral-text-icon-disabled-darker);
  cursor: not-allowed;
}

.button--primary.button--loading {
  background-color: var(--neutral-surface-disabled);
  cursor: wait;
}

.button--secondary {
  background: transparent;
  border-radius: var(--corner-radius-c-8);
  border: var(--border-width-b-1) solid var(--primary-border-default);
  color: var(--primary-text-icon-default);
  font-size: var(--body-base-med-size);
  font-weight: var(--body-base-med-weight);
  line-height: var(--line-height-lead-md);
}

.button--secondary.button--disabled {
  color: var(--neutral-text-icon-disabled-darker);
  border-radius: var(--corner-radius-c-8);
  border: var(--border-width-b-1) solid var(--neutral-border-disabled);
  cursor: not-allowed;
}

.button--secondary.button--loading {
  border-color: var(--neutral-border-disabled);
  cursor: wait;
}

.button--tertiary {
  background: transparent;
  border-radius: var(--corner-radius-c-8);
  color: var(--primary-text-icon-default);
  font-size: var(--body-base-med-size);
  font-weight: var(--body-base-med-weight);
  line-height: var(--line-height-lead-md);
}

.button--tertiary.button--disabled {
  color: var(--neutral-text-icon-disabled-darker);
  cursor: not-allowed;
}

.button--tertiary.button--loading {
  color: var(--neutral-text-icon-disabled-darker);
  cursor: not-allowed;
}

.button--danger {
  border-radius: var(--corner-radius-c-8, 8px);
  background: var(--error-surface-default);
  color: var(--error-text-icon-negative);
  font-size: var(--body-base-med-size);
  font-weight: var(--body-base-med-weight);
  line-height: var(--line-height-lead-md);
}

.button--danger.button--disabled {
  border-radius: var(--corner-radius-c-8);
  background: var(--neutral-surface-disabled);
  color: var(--neutral-text-icon-disabled-darker);
  cursor: not-allowed;
}

.button--danger.button--loading {
  background: var(--neutral-surface-disabled);
  cursor: wait;
}

.button--small {
  padding: var(--pad-space-sp-6) var(--pad-space-sp-14);
  font-size: var(--body-small-med-size);
  line-height: var(--line-height-lead-sm);
}

.button--medium {
  padding: var(--pad-space-sp-8) var(--pad-space-sp-16);
  font-size: var(--body-base-med-size);
  line-height: var(--line-height-lead-md);
  height: 2.25em;
}

.button--large {
  padding: var(--pad-space-sp-12) var(--pad-space-sp-24);
  font-size: var(--body-base-med-size);
  height: 2.875em;
}

.button--loading {
  .content {
    opacity: 0;
  }

  .loading-container {
    align-items: center;
    display: flex;
    height: 100%;
    justify-content: center;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
  }

  .loader {
    width: 1.75em;
    aspect-ratio: 2;
    --circle: no-repeat
      radial-gradient(
        circle closest-side,
        var(--neutral-text-icon-default) 90%,
        #0000
      );
    background:
      var(--circle) 0% 50%,
      var(--circle) 50% 50%,
      var(--circle) 100% 50%;
    background-size: calc(100% / 3) 50%;
    animation: loading-animation 1s infinite linear;
  }
}

@media (hover: hover) {
  .button--primary:not(.button--disabled):not(.button--clicked):not(
      .button--loading
    ):hover {
    background-color: var(--primary-surface-hover);
  }

  .button--secondary:not(.button--disabled):not(.button--clicked):not(
      .button--loading
    ):hover {
    background-color: var(--primary-surface-subtle);
  }

  .button--tertiary:not(.button--disabled):not(.button--clicked):not(
      .button--loading
    ):hover {
    color: var(--primary-text-icon-hover);
  }

  .button--danger:not(.button--disabled):not(.button--clicked):not(
      .button--loading
    ):hover {
    background: var(--error-surface-hover);
  }
}

.button--primary.button--clicked {
  background: var(--primary-surface-pressed);
}

.button--secondary.button--clicked {
  background: transparent;
  border-color: var(--primary-border-pressed);
  color: var(--primary-text-icon-pressed);
}

.button--tertiary.button--clicked {
  color: var(--primary-text-icon-pressed);
}

.button--danger.button--clicked {
  background: var(--error-surface-pressed);
}

@keyframes loading-animation {
  20% {
    background-position:
      0% 0%,
      50% 50%,
      100% 50%;
  }
  40% {
    background-position:
      0% 100%,
      50% 0%,
      100% 50%;
  }
  60% {
    background-position:
      0% 50%,
      50% 100%,
      100% 0%;
  }
  80% {
    background-position:
      0% 50%,
      50% 50%,
      100% 100%;
  }
}
</style>
